import {formatCoveragePercent} from "./purchaseCostCoveragePresentation.js";
import {formatCurrencyFromMinorUnits} from "./currencyMinorUnits.js";

/** Select an explicitly supplied matched-line result; never estimate it from whole revenue and known cost. */
export function selectProfitabilityMetric({fullValue, fullComplete, partialProfitability, partialField}) {
    const suppliedPartial = partialProfitability?.[partialField];
    const allowed = fullValue !== undefined || suppliedPartial !== undefined;
    if (!allowed) return {value: undefined, partial: false, estimated: false, allowed: false};

    if (fullComplete && fullValue !== undefined) {
        return {value: fullValue, partial: false, estimated: false, allowed: true};
    }

    const value = partialProfitability?.available === true && Number(partialProfitability.knownLineCount) > 0
        ? suppliedPartial ?? null
        : null;
    return {
        value,
        partial: value !== null && value !== undefined,
        estimated: value !== null && value !== undefined && partialProfitability?.quality === "estimated",
        allowed: true,
    };
}

/** A supplied assigned-spend result owns its value and partial label; never relabel a different standard value. */
export function selectAssignedMarketingMetric({standardSelection, assignedMarketingResult, assignedField, snapshotIncomplete = false, estimated = false}) {
    if (snapshotIncomplete) {
        return {...(standardSelection ?? {value: undefined, partial: false, estimated: false, allowed: false}), assignedMarketing: false};
    }
    if (!assignedMarketingResult) return {...(standardSelection ?? {value: undefined, partial: false, estimated: false, allowed: false}), assignedMarketing: false};

    const value = assignedMarketingResult[assignedField];
    return {
        value: value ?? null,
        partial: true,
        estimated,
        allowed: true,
        assignedMarketing: true,
    };
}

export function selectReportProfitabilityMetric({standardSelection, partialProfitability, assignedField, snapshotIncomplete = false}) {
    return selectAssignedMarketingMetric({
        standardSelection,
        assignedMarketingResult: partialProfitability?.assignedMarketingResult,
        assignedField,
        snapshotIncomplete,
        estimated: partialProfitability?.quality === "estimated",
    });
}

export function partialProfitabilityCoverage(partialProfitability) {
    const coverage = partialProfitability?.revenueCoveragePercent;
    return coverage !== null && coverage !== undefined && Number.isFinite(Number(coverage)) ? Number(coverage) : null;
}

export function formatCzechProductLineCount(value, format = (count) => new Intl.NumberFormat("cs-CZ").format(count)) {
    if (value === null || value === undefined || value === "") return null;
    const count = Number(value);
    if (!Number.isSafeInteger(count) || count < 0) return null;
    const whole = Math.trunc(count);
    const lastTwo = whole % 100;
    const last = whole % 10;
    const unit = whole === 1 ? "řádek" : lastTwo < 12 || lastTwo > 14 ? (last >= 2 && last <= 4 ? "řádky" : "řádků") : "řádků";
    return `${format(count)} ${unit}`;
}

export function partialProfitabilityChartPoint(value, partialProfitability, isPartial, t) {
    if (!isPartial || value === null || value === undefined) return value;
    const coverage = partialProfitabilityCoverage(partialProfitability);
    const label = t("reporting_lab.partial_profitability.label", {defaultValue: "Dílčí výsledek"});
    const coverageText = coverage === null ? "" : t("reporting_lab.partial_profitability.coverage", {defaultValue: "Pokrytí tržeb řádků {{coverage}} %", coverage: formatCoveragePercent(coverage), interpolation: {escapeValue: false}});
    const estimate = partialProfitability?.quality === "estimated" ? t("reporting_lab.poas_unavailable.estimated_short", {defaultValue: "Odhad"}) : "";
    return {value, metricNote: [label, coverageText, estimate].filter(Boolean).join(" · ")};
}

export function assignedMarketingChartPoint(value, partialProfitability, t, metric = "profit") {
    const result = partialProfitability?.assignedMarketingResult;
    if (value === null || value === undefined || !result) return value;
    const basis = result.grossProfitBasis === "full"
        ? t("reporting_lab.partial_profitability.assigned_basis_full", {defaultValue: "úplného hrubého zisku"})
        : t("reporting_lab.partial_profitability.assigned_basis_known_lines", {defaultValue: "hrubého zisku známých řádků"});
    const amount = result.assignedMarketingSpend?.amount;
    const currency = result.assignedMarketingSpend?.currency;
    const formattedAmount = formatCurrencyFromMinorUnits(amount, currency);
    const estimated = partialProfitability?.quality === "estimated"
        ? t("reporting_lab.poas_unavailable.estimated_short", {defaultValue: "Odhad"})
        : "";
    return {
        value,
        metricNote: [metric === "poas"
            ? t("reporting_lab.partial_profitability.assigned_chart_poas_note", {
                defaultValue: "Dílčí POAS = {{basis}} / přiřazená reklama; přiřazená reklama {{amount}}.",
                basis,
                amount: formattedAmount,
            })
            : t("reporting_lab.partial_profitability.assigned_chart_profit_note", {
                defaultValue: "Dílčí výsledek = {{basis}} − přiřazená reklama; přiřazená reklama {{amount}}.",
                basis,
                amount: formattedAmount,
            }), estimated].filter(Boolean).join(" · "),
    };
}

/** Percentage over the same matched known-line revenue, never the whole-period revenue. */
export function partialProfitabilityMarginPercent(data, {gross = false} = {}) {
    if (data?.available !== true || !(Number(data.knownLineCount) > 0)) return null;
    const revenue = data.knownRevenue?.amount;
    const result = (gross ? data.knownGrossProfit : data.knownProfitAfterAllMarketing)?.amount;
    if (revenue === null || revenue === undefined || result === null || result === undefined
        || !Number.isFinite(Number(revenue)) || !Number.isFinite(Number(result)) || Number(revenue) <= 0) return null;
    return Number(result) / Number(revenue) * 100;
}

